/**
 * Offline photo queue: when a photo can't be uploaded (offline or failure) its
 * blob is stored in IndexedDB and a `local-photo://<uuid>` marker takes its place
 * in the inspection's photo_urls. On reconnect the sync engine uploads the blob,
 * gets the real URL, and replaces the marker everywhere. Pure helpers (markers +
 * replacement) are unit-tested; blob I/O is thin idb wrapping.
 *
 * INVARIANT: a marker must never reach the server. Sync resolves markers before
 * pushing, and pushLocalDraftToServer strips any residual marker as a backstop.
 */
import { STORE_PHOTO_BLOBS, idbGet, idbGetAll, idbPut, idbDelete } from './idb';
import type { LocalDraft } from './localDraftStore';
import { rekeyPhotoGeo, type PhotoGeoMap } from './photoLocation';

export const MARKER_PREFIX = 'local-photo://';

export interface QueuedPhoto {
  id: string;
  blob: Blob;
  bucket: string;
  path: string;
  localDraftId: string;
  createdAt: number;
}

// ---- pure (unit-tested) ----------------------------------------------------

export function makeMarker(id: string): string {
  return `${MARKER_PREFIX}${id}`;
}

export function isLocalMarker(url: string): boolean {
  return typeof url === 'string' && url.startsWith(MARKER_PREFIX);
}

export function markerId(marker: string): string {
  return marker.slice(MARKER_PREFIX.length);
}

/** Remove any unresolved `local-photo://` markers from a photo_urls array. */
export function stripUnresolvedMarkers(urls: string[]): string[] {
  return (urls ?? []).filter((u) => !isLocalMarker(u));
}

/**
 * Return a new draft with `marker` replaced by `realUrl` across every
 * photo_urls — and across every photo_geo, which is keyed by the same string.
 *
 * Re-keying the geo map here is load-bearing: an offline photo is recorded
 * under its local-photo:// marker at capture time, so if only the URL array
 * were swapped, the coordinates would orphan under a marker that no longer
 * exists and the photo would silently lose its pin the moment it uploaded.
 * That is the failure mode this whole path is most likely to hit, because it
 * only happens to photos taken with no signal.
 */
export function replaceMarkerEverywhere(draft: LocalDraft, marker: string, realUrl: string): LocalDraft {
  const swap = (urls: string[]) => urls.map((u) => (u === marker ? realUrl : u));
  const swapGeo = (geo: PhotoGeoMap | undefined) =>
    geo && geo[marker] ? rekeyPhotoGeo(geo, marker, realUrl) : geo;
  return {
    ...draft,
    controllerInspections: draft.controllerInspections.map((c) => ({
      ...c,
      photo_urls: swap(c.photo_urls),
      photo_geo: swapGeo(c.photo_geo),
    })),
    doublecheckInspections: draft.doublecheckInspections.map((d) => ({
      ...d,
      photo_urls: swap(d.photo_urls),
      photo_geo: swapGeo(d.photo_geo),
    })),
    zoneInspections: draft.zoneInspections.map((z) => ({
      ...z,
      issues: z.issues.map((i) => ({
        ...i,
        photo_urls: swap(i.photo_urls),
        photo_geo: swapGeo(i.photo_geo),
      })),
    })),
  };
}

// ---- IDB blob queue --------------------------------------------------------

/** Store a photo blob locally; returns the marker to drop into photo_urls. */
export async function enqueuePhoto(args: {
  blob: Blob;
  bucket: string;
  path: string;
  localDraftId: string;
}): Promise<string> {
  const id = crypto.randomUUID();
  const record: QueuedPhoto = { id, ...args, createdAt: Date.now() };
  await idbPut(STORE_PHOTO_BLOBS, record);
  return makeMarker(id);
}

export function getQueuedPhoto(id: string): Promise<QueuedPhoto | null> {
  return idbGet<QueuedPhoto>(STORE_PHOTO_BLOBS, id);
}

export function getAllQueuedPhotos(): Promise<QueuedPhoto[]> {
  return idbGetAll<QueuedPhoto>(STORE_PHOTO_BLOBS);
}

export function deleteQueuedPhoto(id: string): Promise<void> {
  return idbDelete(STORE_PHOTO_BLOBS, id);
}

/**
 * Delete every queued photo belonging to one draft. Call this whenever a draft is
 * removed (submit success, or an already-submitted 403) so its photo blobs don't
 * outlive it as orphans the sync engine can never drain. Returns the count removed.
 */
export async function deleteQueuedPhotosForDraft(localDraftId: string): Promise<number> {
  const mine = (await getAllQueuedPhotos()).filter((q) => q.localDraftId === localDraftId);
  for (const q of mine) await deleteQueuedPhoto(q.id);
  return mine.length;
}

/**
 * Pure: which queued photos no longer have a live draft to upload them? A photo is
 * orphaned once its owning draft is gone — it would otherwise sit in the queue
 * forever, since photos only upload from inside that draft's sync.
 */
export function selectOrphanPhotos(photos: QueuedPhoto[], liveDraftIds: Set<string>): QueuedPhoto[] {
  return photos.filter((q) => !liveDraftIds.has(q.localDraftId));
}

/** Object URL for a queued blob, to render a live preview of a pending photo. */
export async function resolveMarkerToObjectUrl(marker: string): Promise<string | null> {
  const queued = await getQueuedPhoto(markerId(marker));
  return queued ? URL.createObjectURL(queued.blob) : null;
}
